import { Checkbox, RadioGroup } from 'react-component-library'
import { DataEntryStep1Props } from 'src/types'
import { get_error } from 'src/utils'
import { InputSelect } from 'src/widgets/input-select/input-select'
import { InputText } from 'src/widgets/input-text/input-text'
import { Tooltip } from 'src/widgets/tooltip/tooltip'
import { useDataEntryStep1 } from './data-entry-step1.hook'
import { InputDate } from 'src/widgets/input-date/input-date'
import { InputRadio } from 'src/widgets/input-radio/input-radio'
import { ChangeEvent } from 'react'
import { InputCheckbox } from 'src/widgets/input-checkbox/input-checkbox'

export const DataEntryStep1 = (props: DataEntryStep1Props) => {
  const {
    t,
    coverageOptions,
    coverageStartDateDisabledFrom,
    coverageStartDateDisabledTo,
    coverageEndDateDisabledFrom,
    coverageEndDateDisabledTo,
    closePickerTrigger,
    toggleClosePickerTrigger,
  } = useDataEntryStep1(props)

  const {
    daysOptions,
    dailyOptions,
    formik: {
      values,
      errors,
      touched,
      getFieldProps,
      setFieldValue,
      setFieldTouched,
      handleChange,
      handleBlur,
    },
    validationSchema,
    insuredLimit,
    minMaxContractor,
  } = props

  const CoverageRadio = (
    <div className="col-sm-12 p-0 px-md-3 pl-md-3 mb-2 mb-md-1 font-14 coverage-radio">
      <InputRadio
        name="coverage_type_radios"
        handleChange={handleChange}
        handleBlur={handleBlur}
        options={coverageOptions}
        validationSchema={validationSchema}
        touched={touched.coverage_type_radios}
        value={values.coverage_type_radios}
        error={errors.coverage_type_radios}
        label={t('data_entry_step_1.coverage_type_title')}
        classes="m-0 p-0"
        classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-3"
        vertically={true}
      />
    </div>
  )

  const CoverageInsuredNumber = (
    <div className="col-12 col-md-6 p-0 px-md-3 pl-md-3 pr-md-3">
      <InputText
        name="insured_number"
        handleChange={handleChange}
        handleBlur={handleBlur}
        validationSchema={validationSchema}
        touched={touched.insured_number}
        value={values.insured_number}
        error={errors.insured_number}
        type="number"
        label={t('data_entry_step_1.coverage_insured_number')}
        placeholder={t('data_entry_step_1.coverage_insured_number_placeholder')}
        classesError="font-15 m-0 c-862633"
        classesLabel="font-15"
      />
    </div>
  )

  const PeriodTypeDates = (
    <div className='d-md-flex'>
      <div className="col-sm-12 p-0 m-0 px-md-3">
        <InputDate
          name="coverage_start_date"
          handleChange={handleChange}
          handleBlur={handleBlur}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          validationSchema={validationSchema}
          touched={touched.coverage_start_date}
          value={values.coverage_start_date}
          error={errors.coverage_start_date}
          label={t('data_entry_step_1.coverage_start_date')}
          placeholder={t('data_entry_step_1.coverage_start_date_placeholder')}
          disabled={values.insured_days === 'daily_1'}
          disabledFrom={coverageStartDateDisabledFrom}
          disabledTo={coverageStartDateDisabledTo}
          classes="m-0 p-0"
          closePickerTrigger={closePickerTrigger}
        />
      </div>

      <div className="col-sm-12 p-0 m-0 px-md-3">
        <InputDate
          name="coverage_end_date"
          handleChange={handleChange}
          handleBlur={handleBlur}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          validationSchema={validationSchema}
          touched={touched.coverage_end_date}
          value={values.coverage_end_date}
          error={errors.coverage_end_date}
          label={t('data_entry_step_1.coverage_end_date')}
          placeholder={t('data_entry_step_1.coverage_end_date_placeholder')}
          disabled={true}
          disabledFrom={coverageEndDateDisabledFrom}
          disabledTo={coverageEndDateDisabledTo}
          classes="m-0 p-0"
          closePickerTrigger={closePickerTrigger}
        />
      </div>
    </div>
  )

  const InsuredDays = (
    <div>
      <div className="col-12 d-md-flex">
        <div className="col-12 col-md-6 p-0 px-md-3 pl-md-3 pr-md-3">
          <InputSelect
            name="insured_days"
            validationSchema={validationSchema}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            options={daysOptions}
            touched={touched.insured_days}
            value={values.insured_days}
            error={errors.insured_days}
            label={t('data_entry_step_1.coverage_insured_days')}
          />
        </div>
        {values?.insured_days === 'daily' && (
          <div className="col-12 col-md-6 p-0 px-md-3 pl-md-3 pr-md-3">
            <InputSelect
              name="insured_daily"
              validationSchema={validationSchema}
              setFieldValue={setFieldValue}
              options={dailyOptions}
              touched={touched.insured_daily}
              value={values.insured_daily}
              error={errors.insured_daily}
              label={`${t('data_entry_step_1.coverage_insured_daily')}*`}
            />
          </div>
        )}
      </div>
      {Number(values?.insured_number) > insuredLimit && (
        <div className="col-12 col-md-6 p-0 m-0 pb-3">{PeriodTypeDates}</div>
      )}
    </div>
  )

  const FamilyField = (
    <div className="col-sm-12 p-0 ps-md-3 pe-md-3">
      <div className="mb-4 mt-3 d-flex family-checkbox">
        <InputCheckbox
          name="is_family"
          handleChange={handleChange}
          handleBlur={handleBlur}
          validationSchema={validationSchema}
          touched={touched.is_family}
          value={values.is_family}
          error={errors.is_family}
          label={t("data_entry_step_3.is_family")}
          classesLabel="m-0 font-20 d-flex align-items-baseline white-space-pre me-2"
          disabled={
            Number(values?.insured_number) <= 1 ||
            Number(values?.insured_number) > (minMaxContractor?.max || 0) ||
            (get_error(errors?.insured_number) !== '' &&
              get_error(errors?.insured_number) !== undefined &&
              touched?.insured_number)
          }
        />
        <Tooltip placement="top">
          {<div className="font-14">{t('data_entry_step_3.is_family_tooltip_label')}</div>}
        </Tooltip>
      </div>
    </div>
  )

  return (
    <div>
      {CoverageRadio}
      {CoverageInsuredNumber}
      {InsuredDays}
      {FamilyField}
    </div>
  )
}
